import {
  isPublishingDisabledError,
  useGetConnections
} from './use-get-connections';
import { useGetAllowedPlatforms } from './use-get-allowed-platforms';

/**
 * Single source of truth for "is the publishing feature available to
 * this org?" (#2302 + #2303).
 *
 * Piggybacks on the connections query cache so the editors / About
 * panels do not fire an extra round trip just to decide whether to
 * render the Publish surface — if the Connections tab already loaded
 * the list, this is a cache read. The backend signals "off" with
 * 404 + ``detail: publishing-not-enabled`` (see #4099's
 * ``require_publishing_enabled``) so a hardcoded entitlement would
 * only ever be right by accident.
 *
 * Scriptix staff can also allow publishing but block every platform
 * (``publishing.allowed_platforms`` = []). Then there is nothing to publish
 * to, so the Publish surface is hidden too. While that list is loading or
 * failed it is ignored; the server still refuses a blocked platform.
 *
 * ``isEnabled`` returns ``true`` while the query is still loading —
 * showing the Publish button briefly before hiding it on a confirmed
 * disable is less jarring than withholding it on every editor cold-load.
 */
export function useIsPublishingEnabled(enabled: boolean = true): {
  isEnabled: boolean;
  isLoading: boolean;
} {
  const { error, isLoading } = useGetConnections(enabled);
  const { data: allowedPlatforms } = useGetAllowedPlatforms(enabled);
  const noPlatformsAllowed = allowedPlatforms?.length === 0;
  return {
    isEnabled: !isPublishingDisabledError(error) && !noPlatformsAllowed,
    isLoading
  };
}
